import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

export const styles = (theme: Theme) => {
	// The slug block's column and gap. The Default IDX Page has no toggle to line up with, so it uses this
	// as is; the other pages indent it (`disclosed`), so the spacing can't drift between them.
	const disclosedFlush: CSSObject = {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2),
	};
	return ({
		divider: {
			borderColor: theme.palette.custom.divider,
		},
		body: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(3),
		},
		section: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
		},
		// Web/H5, 18/700: v3 sets the modal's sub-heads a step above the 16/700 the MAX-4419 kit gave them.
		sectionTitle: {
			fontSize: 18,
			fontWeight: 700,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		sectionDescription: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
			marginTop: theme.spacing(-1),
		},
		// The instruction above the slug row ("Enter your custom slug below ... We recommend using /listing.").
		instruction: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// v3: the site address is plain text beside the input, not an adornment inside it, and the input
		// shows its slash. Items sit at the top so a validation message under the input doesn't drag the
		// prefix down.
		slugRow: {
			display: "flex",
			alignItems: "flex-start",
			gap: theme.spacing(1),
		},
		// Lined up with the input's text, which starts 12px below the input's top edge.
		slugPrefix: {
			flexShrink: 0,
			paddingTop: 12,
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textSecondary,
		},
		// The read-only slug: the whole address in one box, the slug in bold, a pencil at the right.
		urlBox: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(1),
			maxWidth: 840,
			padding: "8px 8px 8px 16px",
			border: `1px solid ${theme.palette.custom.border}`,
			borderRadius: 4,
		},
		urlText: {
			minWidth: 0,
			overflowWrap: "anywhere",
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// Level with the input (48px tall), not with a validation message under it.
		doneButton: {
			flexShrink: 0,
			height: 48,
		},
		// The input and Reset to default under it, so Reset lines up with the input rather than the prefix.
		slugColumn: {
			flex: "0 1 546px",
			minWidth: 0,
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1),
		},
		slugInput: {
			minWidth: 0,
			// The shared field outlines a focused input in the brand orange, which reads as an error beside
			// this field's real (red) error state. Gray while focused is deliberate (Dave's call, not the
			// design kit's orange). The shared focus rule also beats the error red, so a focused error is
			// set to red here.
			"& .Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.textSecondary,
			},
			"& .Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.error.main,
			},
		},
		// Figma's warning banner: pale yellow, 16px padding, radius 4, the alert icon beside the text.
		warning: {
			display: "flex",
			gap: theme.spacing(1),
			padding: theme.spacing(2),
			borderRadius: 4,
			backgroundColor: theme.palette.custom.tagWarningSurface,
			color: theme.palette.custom.textPrimary,
			fontSize: 16,
			lineHeight: "24px",
		},
		warningIcon: {
			flexShrink: 0,
			marginTop: theme.spacing(0.5),
			color: theme.palette.custom.tagWarningText,
		},
		// The label and its Recommended tag, side by side.
		toggleLabel: {
			display: "inline-flex",
			alignItems: "center",
			gap: theme.spacing(1),
		},
		// The Custom URL field and what goes with it, lined up with the toggle's label. 59px is the
		// shared Switch's layout (a -11 control margin, 8 padding, the 46px track, 8 and 8 around the
		// label), confirmed in the browser: the field's label and the toggle's label share an x.
		disclosed: {
			...disclosedFlush,
			paddingLeft: 59,
		},
		disclosedFlush,
		// Reset to default, directly below the input and left-aligned with it.
		resetRow: {
			display: "flex",
			justifyContent: "flex-start",
		},
		// Figma's tertiary button: teal text, no padding of its own.
		tertiaryButton: {
			minWidth: 0,
			padding: 0,
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.tertiaryAction,
			"&:hover": {
				backgroundColor: "transparent",
				textDecoration: "underline",
			},
		},
		// The slug box's edit pencil, in the interactive teal rather than the icon button's default.
		pencil: {
			color: theme.palette.custom.tertiaryAction,
		},
		// The discard confirm's body, at the refreshed modals' body size (as Switch versions).
		discardText: {
			fontSize: 18,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
	} satisfies Record<string, CSSObject>);
};
